/**
 * AG-UI → bespoke StreamEvent (client-side inverse of the server bridge's
 * `translateStreamEvent`, features/agent/server/agui-bridge.ts).
 *
 * WHY (Phase 4b-1, strangler migration — non-hacky):
 * `useAgentSession` already has a battle-tested 25-case StreamEvent reducer +
 * 12 transport-agnostic methods. Rewriting that against AG-UI would duplicate
 * ~90% of working code to swap ~10% of ingress. Instead we keep AG-UI as the
 * standard wire (new surfaces / CopilotKit / miniapps consume it directly),
 * and let the *existing* ChatPanel reducer be reused by translating the AG-UI
 * stream back into the StreamEvent shape it already consumes. Adapt at the
 * edge, preserve the proven core.
 *
 * Pure + stateful-by-instance ⇒ unit-testable, no React. One instance per run.
 *
 * Seam note: AG-UI's standard events do NOT carry papan-domain fields
 * (sessionCost, tokens, estimatedTokens, revertState, todos, summary…).
 * `complete` is emitted with zeroed cost/tokens here; the AG-UI ingress layer
 * (4b-1b) refreshes those via REST / bridge Custom events — a deliberate seam,
 * recorded in the plan, not a hack.
 */
import type { SessionProcessor } from '../core/processor.js'
import { isSuppressedTransportTool } from './registerAgentTool.js'
// Self-import the registrations: `isSuppressedTransportTool` below is
// only correct if the registry is populated. Depending on the consumer
// (hooks.ts) to side-effect-import this would silently un-suppress HITL
// transport tools (phantom "Running… 0.3K" blocks) if a future consumer
// forgot it. Idempotent (module loaded once); registration is pure (the
// db_editor handler only touches `window` when invoked).
import './agentTools.js'

type SE = SessionProcessor.StreamEvent
type Agui = { type: string; [k: string]: unknown }

const ZERO_TOKENS = { input: 0, output: 0, reasoning: 0, cache: { read: 0, write: 0 } }

export interface AguiToStream {
  /** Translate one AG-UI event into zero or more bespoke StreamEvents. */
  push(ev: Agui): SE[]
  /** True once RUN_FINISHED/RUN_ERROR seen (ingress closes the stream). */
  done: boolean
}

/**
 * Best-effort extract a top-level JSON string field from a possibly-incomplete
 * buffer (streamed tool args). Decodes standard JSON escapes; if the buffer
 * ends mid-value, returns what's decoded so far (drops a dangling backslash /
 * truncated \uXXXX). Pure, dependency-free, browser-safe — the project bars
 * @ai-sdk/* partial-JSON utils from the client bundle (see ModelSelector.tsx),
 * and we only need one flat string field, not a general partial parser.
 */
function partialJsonStringField(buf: string, field: string): string | null {
  const key = `"${field}"`
  // Find `key` as an OBJECT KEY (followed by `:`), not as a string VALUE
  // that merely equals the field name. Real models emit the full
  // structured db_editor input, e.g. `{"action":"set_sql","mode":"sql",
  // …,"sql":"…"}` — a naive first `indexOf('"sql"')` matches the VALUE
  // of `"mode":"sql"`, sees `,` instead of `:`, and bails → the SQL is
  // never extracted and no streamed preview ever fires for that model.
  // (The mock omits `mode`, so it happened to work — masking this.)
  // Skip non-key occurrences; JSON keys are unique so the first real
  // key match is THE field.
  let search = 0
  for (;;) {
    const at = buf.indexOf(key, search)
    if (at < 0) return null
    let i = at + key.length
    while (i < buf.length && ' \t\n\r'.includes(buf[i])) i++
    if (buf[i] !== ':') { search = at + 1; continue } // value/substring, not the key
    i++
    while (i < buf.length && ' \t\n\r'.includes(buf[i])) i++
    if (buf[i] !== '"') return null // value not a string / not yet streamed
    i++
    let out = ''
    while (i < buf.length) {
      const ch = buf[i]
      if (ch === '"') return out // closed → complete value
      if (ch === '\\') {
        if (i + 1 >= buf.length) return out // dangling escape at buffer end
        const esc = buf[i + 1]
        if (esc === 'u') {
          if (i + 6 > buf.length) return out // truncated \uXXXX
          out += String.fromCharCode(parseInt(buf.slice(i + 2, i + 6), 16))
          i += 6
        } else {
          out += { n: '\n', t: '\t', r: '\r', b: '\b', f: '\f', '/': '/', '"': '"', '\\': '\\' }[esc] ?? esc
          i += 2
        }
      } else {
        out += ch
        i++
      }
    }
    return out // ran out of buffer → partial value so far
  }
}

export function createAguiToStream(): AguiToStream {
  let sessionId = ''
  let lastMsgId = ''
  const text = new Map<string, string>() // messageId → accumulated content
  const reasoning = new Map<string, string>() // messageId → accumulated reasoning (B(a) mirror of `text`)
  const tools = new Map<string, { name: string; args: string; parent: string; prevSqlLen?: number }>()
  const api: AguiToStream = { done: false, push }
  return api

  function s(e: Omit<SE, 'sessionId'> & Record<string, unknown>): SE {
    return { ...(e as object), sessionId } as SE
  }

  function push(ev: Agui): SE[] {
    const t = ev.type
    if (t === 'RUN_STARTED') {
      sessionId = (ev.threadId as string) || sessionId
      return [s({ type: 'status', status: 'processing' })]
    }
    if (t === 'MESSAGES_SNAPSHOT') {
      // History is (re)hydrated via REST by the ingress layer, not per-event.
      return []
    }
    if (t === 'TEXT_MESSAGE_START') {
      lastMsgId = (ev.messageId as string) || lastMsgId
      text.set(lastMsgId, '')
      return []
    }
    if (t === 'TEXT_MESSAGE_CONTENT') {
      const id = (ev.messageId as string) || lastMsgId
      lastMsgId = id
      const content = (text.get(id) ?? '') + (ev.delta as string ?? '')
      text.set(id, content)
      // B(a): carry the ACCUMULATED snapshot, not just the raw delta, so the
      // client reducer REPLACEs instead of APPENDs. Two live streams for one
      // session (the lost-abort zombie + the resend) then converge to the same
      // text instead of doubling every chunk. `delta` stays for back-compat.
      return [s({ type: 'text-delta', messageId: id, delta: ev.delta as string ?? '', content })]
    }
    if (t === 'TEXT_MESSAGE_END') {
      const id = (ev.messageId as string) || lastMsgId
      const content = text.get(id) ?? ''
      text.delete(id)
      return [s({ type: 'text-done', messageId: id, content })]
    }
    if (t === 'REASONING_MESSAGE_START') {
      const id = (ev.messageId as string) || lastMsgId
      // R1.4: advance the terminal identity from reasoning too. Only TEXT_* used
      // to move lastMsgId, so a reasoning-ONLY run (thinking, no text output) left
      // it '', and RUN_FINISHED then emitted `complete` with messageId '' — which
      // the reducer's duplicate-terminal guard (needs a truthy id) skips, so a
      // duplicated RUN_FINISHED double-counts session cost + re-fires onComplete.
      lastMsgId = id
      reasoning.set(id, '')
      return [s({ type: 'reasoning-start', messageId: id })]
    }
    if (t === 'REASONING_MESSAGE_CONTENT') {
      const id = (ev.messageId as string) || lastMsgId
      lastMsgId = id // R1.4 (see REASONING_MESSAGE_START)
      // B(a): mirror the text accumulator so reasoning deltas are snapshot-based
      // too (same double-application hazard as text under two streams).
      const content = (reasoning.get(id) ?? '') + (ev.delta as string ?? '')
      reasoning.set(id, content)
      return [s({ type: 'reasoning-delta', messageId: id, delta: ev.delta as string ?? '', content })]
    }
    if (t === 'REASONING_MESSAGE_END') {
      const id = (ev.messageId as string) || lastMsgId
      reasoning.delete(id)
      return [s({ type: 'reasoning-end', messageId: id })]
    }
    // PAPAN EXTENSION: the worker dispatching a client-executed tool with the
    // args IT resolved (share_link resolves a path to a file id).
    // Distinct from the model's TOOL_CALL_START below, whose `input` is the raw
    // model args — a different shape.
    //
    // Dropped unless it carries BOTH ids: the worker keys its wait on
    // `toolCallId`, so a dispatch missing one can never be answered — emitting
    // it would strand the run instead of failing it.
    if (t === 'CUSTOM' && ev.name === 'papan.client-tool-request') {
      const v = (ev.value ?? {}) as { toolCallId?: string; toolName?: string; args?: Record<string, unknown>; workspaceId?: unknown; waitId?: unknown }
      if (!v.toolCallId || !v.toolName) return []
      // #352 R7/R9: the session's workspace and the wait id, when the worker sent them.
      const workspaceId = typeof v.workspaceId === 'string' && v.workspaceId ? v.workspaceId : undefined
      const waitId = typeof v.waitId === 'string' && v.waitId ? v.waitId : undefined
      // `s()` stamps the stream's own tracked sessionId last, so this '' is a
      // type placeholder and is never observed by a consumer.
      return [s({ type: 'client-tool-request', sessionId: '', toolCallId: v.toolCallId, toolName: v.toolName, args: v.args ?? {}, ...(workspaceId ? { workspaceId } : {}), ...(waitId ? { waitId } : {}) }) as SE]
    }
    // #384: a pending permission request timed out → drop its dead prompt.
    if (t === 'CUSTOM' && ev.name === 'papan.permission-expired') {
      const v = (ev.value ?? {}) as { requestId?: string }
      if (!v.requestId) return []
      return [s({ type: 'permission-expired', sessionId: '', requestId: v.requestId }) as SE]
    }
    // #637 Parallel: a pending question resolved elsewhere (answered/dismissed in
    // another tab) → drop its now-dead prompt.
    if (t === 'CUSTOM' && ev.name === 'papan.question-expired') {
      const v = (ev.value ?? {}) as { requestId?: string }
      if (!v.requestId) return []
      return [s({ type: 'question-expired', sessionId: '', requestId: v.requestId }) as SE]
    }
    // PAPAN EXTENSION: live tool-metadata → merge into the running tool-call card.
    if (t === 'CUSTOM' && ev.name === 'papan.tool-metadata') {
      const v = (ev.value ?? {}) as { messageId?: string; callId?: string; metadata?: Record<string, unknown> }
      if (!v.messageId || !v.callId) return []
      return [s({ type: 'tool-metadata', messageId: v.messageId, callId: v.callId, metadata: v.metadata ?? {} }) as SE]
    }
    if (t === 'TOOL_CALL_START') {
      const id = ev.toolCallId as string
      const parent = (ev.parentMessageId as string) || lastMsgId
      // R1.4: a tool-ONLY run (tool calls, no text/reasoning) must also give
      // `complete` a non-empty terminal id — the tool's parent assistant message.
      // Without this its terminal is '' and the reducer's dedup can't catch a
      // duplicated RUN_FINISHED (double-counted cost). Suppressed HITL transport
      // tools carry a valid parent too, so update unconditionally.
      if (parent) lastMsgId = parent
      const name = (ev.toolCallName as string) ?? '?'
      tools.set(id, { name, args: '', parent })
      // papan_question / papan_permission are HITL *transport* (the bridge
      // wraps the request as a tool call), NOT real tools. Tracking them
      // in `tools` lets TOOL_CALL_END rebuild the request from accumulated
      // args, but they must NOT create a tool-call UI block — otherwise
      // the reducer renders a pending block that never closes
      // (TOOL_CALL_END emits a question/permission-request, not a
      // tool-call-done) → the stuck "Running… 0.3K" phantom. The
      // suppression list is the registry (`transport: 'out-of-band'`),
      // not a hard-coded name list — Design note I. db_editor is NOT
      // here: it is a real client-executed tool ('client-executed') and
      // renders as a normal block.
      if (isSuppressedTransportTool(name)) return []
      return [s({ type: 'tool-input-start', messageId: parent, callId: id, toolName: name })]
    }
    if (t === 'TOOL_CALL_ARGS') {
      const id = ev.toolCallId as string
      const tc = tools.get(id)
      if (!tc) return []
      tc.args += (ev.delta as string ?? '')
      if (isSuppressedTransportTool(tc.name)) return [] // accumulate only — no phantom block
      const out: SE[] = [s({ type: 'tool-input-delta', messageId: tc.parent, callId: id, inputSize: tc.args.length, delta: ev.delta as string ?? '' })]
      // AG-UI-native live preview: as the db_editor tool's input args stream
      // in (BEFORE the tool executes), surface the growing `sql` to the
      // editor — the "watch the agent type SQL" effect (capability ②). This
      // is the same streamed-args a CopilotKit renderer would consume;
      // transient + reply-less. The authoritative apply happens at
      // TOOL_CALL_END via the db_editor client-executed handler. Narrow
      // dedicated SE variant (formerly synthesised as
      // editor-action-request{preview:true} — that path is dead with the
      // legacy EditorAction RPC deletion).
      if (tc.name === 'db_editor') {
        const action = partialJsonStringField(tc.args, 'action')
        if (action === 'set_sql' || action === 'navigate') {
          const sql = partialJsonStringField(tc.args, 'sql')
          if (sql && sql.length > (tc.prevSqlLen ?? 0)) {
            tc.prevSqlLen = sql.length
            out.push(s({ type: 'editor-action-preview', sql }) as SE)
          }
        }
      }
      return out
    }
    if (t === 'TOOL_CALL_END') {
      const id = ev.toolCallId as string
      const tc = tools.get(id)
      if (!tc) return []
      let parsed: Record<string, unknown> = {}
      try { parsed = tc.args ? JSON.parse(tc.args) : {} } catch { parsed = {} }
      if (tc.name === 'papan_question') {
        return [s({ type: 'question-request', request: parsed as unknown }) as SE]
      }
      if (tc.name === 'papan_permission') {
        return [s({ type: 'permission-request', request: parsed as unknown }) as SE]
      }
      const realId = ev.papanRealCallId as string | undefined
      if (realId) {
        // Streamed: rename placeholder T→R via replacesCallId, exactly like
        // bespoke. The reducer merges the pending part by replacesCallId and
        // renames its callId to R, so it matches the persisted/reloaded part
        // (callId R) → ONE block, not two. Re-key so the RESULT (arrives
        // under R) still finds this tool.
        tools.delete(id)
        tools.set(realId, tc)
        return [s({ type: 'tool-call-start', messageId: tc.parent, callId: realId, toolName: tc.name, input: parsed, replacesCallId: id })]
      }
      return [s({ type: 'tool-call-start', messageId: tc.parent, callId: id, toolName: tc.name, input: parsed })]
    }
    if (t === 'TOOL_CALL_RESULT') {
      const id = ev.toolCallId as string
      const tc = tools.get(id)
      tools.delete(id)
      // papanTool* = bridge passthrough fields (see agui-bridge tool-call-done)
      // carrying live tool metadata so rich renders (diffs, a2ui, durations)
      // appear during the stream — the reducer already reads title/metadata.
      return [s({
        type: 'tool-call-done',
        // A call started in an EARLIER run (a resumed or superseded suspended call,
        // #540/#814) is unknown here: use the bridge's messageId — the call's own
        // message — before falling back to the newest one, or the result lands on
        // the wrong message and the old card keeps showing "Writing…".
        messageId: tc?.parent ?? (ev.messageId as string | undefined) ?? lastMsgId,
        callId: id,
        output: ev.content as string ?? '',
        ...(ev.papanToolError ? { error: ev.papanToolError as string } : {}),
        ...(ev.papanToolTitle ? { title: ev.papanToolTitle as string } : {}),
        ...(ev.papanToolMetadata ? { metadata: ev.papanToolMetadata as Record<string, unknown> } : {}),
        // #874: the live result was cut to one frame; the saved part loads at turn end.
        ...(ev.papanToolOversized ? { oversized: ev.papanToolOversized as { chars: number } } : {}),
      })]
    }
    if (t === 'STEP_STARTED' || t === 'STEP_FINISHED') {
      return [s({ type: 'step', messageId: lastMsgId, step: Number(ev.stepName) || 0, action: t === 'STEP_STARTED' ? 'start' : 'finish' })]
    }
    if (t === 'RUN_FINISHED') {
      api.done = true
      // papanCost / papanSubagentCost are passthrough fields the bridge
      // attaches (AG-UI has no cost field). Feed them into `complete` so the
      // reducer computes completedCost = cost + subagentCost exactly like the
      // bespoke path — the per-message footer ($X.XX, gated on cost > 0) and
      // the subagent rollup render. Tokens stay zeroed (refreshed via the
      // REST /usage seam). Absent (older bridge) ⇒ 0, same as before.
      //
      // Gate 1 (0177): papanCostBillingMicro / papanCostCurrency carry the
      // billing-native rollup. Without them the per-message BADGE stays
      // hidden after complete — the reducer sets msg.cost from the streamed
      // event but has no billing fields to set, and the badge condition
      // `msg.costBillingMicro != null && amount > 0` never becomes true
      // until an unrelated event fires loadMessages. Absent (older bridge)
      // ⇒ undefined, badge waits for the next loadMessages tick as before.
      const cost = typeof ev.papanCost === 'number' ? ev.papanCost : 0
      const subagentCost = typeof ev.papanSubagentCost === 'number' ? ev.papanSubagentCost : undefined
      const costBillingMicro = typeof ev.papanCostBillingMicro === 'number' ? ev.papanCostBillingMicro : undefined
      const costCurrency = typeof ev.papanCostCurrency === 'string' ? ev.papanCostCurrency : undefined
      return [s({ type: 'complete', messageId: lastMsgId, cost,
        ...(subagentCost !== undefined ? { subagentCost } : {}),
        ...(costBillingMicro !== undefined ? { costBillingMicro } : {}),
        ...(costCurrency !== undefined ? { costCurrency } : {}),
        tokens: ZERO_TOKENS,
      })]
    }
    if (t === 'RUN_ERROR') {
      api.done = true
      const code = ev.code as string | undefined
      if (code === 'CANCELLED') return [s({ type: 'cancelled' })]
      return [s({ type: 'error', error: (ev.message as string) ?? 'error' })]
    }
    if (t === 'CUSTOM' && ev.name === 'papan.file-changed') {
      // The bridge forwards the agent's own file changes as this CUSTOM
      // event (provenance-consistent — only AI tool/bash sync changes).
      // Map back to the existing `file-changed` StreamEvent so the
      // reducer builds the synthetic patch part LIVE → `hasChanges`
      // flips → the "Revert AI changes" / "Restore versions" menu
      // appears without a page refresh. No reducer change.
      const v = (ev.value ?? {}) as { fileId?: string; path?: string; action?: string }
      if (!v.path) return []
      return [s({
        type: 'file-changed',
        fileId: v.fileId ?? '',
        path: v.path,
        action: (v.action as 'created' | 'updated' | 'deleted') ?? 'updated',
      }) as SE]
    }
    if (t === 'CUSTOM' && ev.name === 'papan.file-sync-failed') {
      // A file the agent wrote in the sandbox could not be committed to the
      // drive (quota / disk full). Map back to the `file-sync-failed`
      // StreamEvent so the reducer records a non-fatal per-file warning —
      // WITHOUT it the file is silently lost behind a "created" claim.
      const v = (ev.value ?? {}) as { path?: string; reason?: string; code?: string }
      if (!v.path || !v.reason) return []
      return [s({ type: 'file-sync-failed', path: v.path, reason: v.reason, code: v.code }) as SE]
    }
    if (t === 'CUSTOM' && ev.name === 'papan.turn-activity') {
      // Work between tool calls — today the post-`bash` tree sync. `activity`
      // is nullable BY DESIGN (null clears the indicator), so this must not use
      // the usual "missing field → drop the event" guard the others do: dropping
      // the clear would strand a "Syncing…" label for the rest of the session.
      const v = (ev.value ?? {}) as {
        activity?: { kind?: string; phase?: string; done?: number; total?: number } | null
      }
      const a = v.activity
      if (a == null) return [s({ type: 'turn-activity', activity: null }) as SE]
      // A malformed payload is treated as a CLEAR rather than ignored, for the
      // same reason: the failure mode to avoid is a label that never goes away.
      if (a.kind !== 'sync' || typeof a.done !== 'number' || typeof a.total !== 'number') {
        return [s({ type: 'turn-activity', activity: null }) as SE]
      }
      return [s({
        type: 'turn-activity',
        activity: {
          kind: 'sync',
          phase: (a.phase as 'walk' | 'hash' | 'sync' | 'delete') ?? 'sync',
          done: a.done,
          total: a.total,
        },
      }) as SE]
    }
    if (t === 'CUSTOM' && ev.name === 'papan.compaction') {
      // The turn is pausing to compact, or resuming after. `active: true`
      // additionally carries the streamed message so the reducer can COMMIT it
      // without the idle transition an ordinary `complete` performs — the two
      // used to be fused, which is why keeping the run open needed this split.
      const v = (ev.value ?? {}) as { active?: boolean; messageId?: string; cost?: number; tokens?: unknown }
      if (v.active !== true) return [s({ type: 'compaction-progress', active: false }) as SE]
      // TWO worker events collapse onto this one CUSTOM name and only one of
      // them carries a message to commit: the processor's `compactPending`
      // complete (has `messageId`), and the worker's own indicator publish
      // (has none). Synthesising a `complete` for the second is destructive —
      // an empty id makes the reducer fall back to "the last assistant
      // message", overwriting its real cost with 0, re-firing onComplete, and
      // setting `lastCompletedMessageId` to `''`, which is the dedupe key
      // `resolveClaimedTerminal` needs to recognise the terminal later.
      if (!v.messageId) return [s({ type: 'compaction-progress', active: true }) as SE]
      // TWO events, in this order and deliberately so. The pause has to do
      // everything an ordinary `complete` does to the interrupted message —
      // finalise stale tool calls, stamp cost and timestamps — and exactly one
      // thing differently: the turn has NOT ended. Reusing the real `complete`
      // reducer and then correcting `status` keeps ONE implementation of that
      // commit; copying it here is how the two would drift.
      return [
        // `compactPending` — the same marker the server sets. It tells the
        // reducer this `complete` is a PAUSE borrowing the commit, not a turn
        // ending, which is what keeps the deferred resync deferred.
        s({ type: 'complete', messageId: v.messageId ?? '', cost: v.cost ?? 0, tokens: v.tokens, compactPending: true }),
        s({ type: 'compaction-progress', active: true, messageId: v.messageId }),
      ] as SE[]
    }
    if (t === 'CUSTOM' && ev.name === 'papan.cost-update') {
      // The bridge forwards this BEFORE the terminal RUN_FINISHED so we
      // get it while the stream is alive. Reducer handles by calling
      // loadSessionCost, which fetches /usage now that the worker has
      // committed the rolled-up message.cost — see processor.ts
      // `cost-update` definition + the comment block in agent.ts.
      const v = (ev.value ?? {}) as { sessionId?: string; messageId?: string }
      if (!v.sessionId || !v.messageId) return []
      return [s({
        type: 'cost-update',
        sessionId: v.sessionId,
        messageId: v.messageId,
      }) as SE]
    }
    if (t === 'CUSTOM' && ev.name === 'papan.subagent-update') {
      // Yield-port Day 10b-d UI follow-up. The bridge forwards this each
      // time the parent's SessionBus.subscribeChild filter selects a
      // child-session event for forwarding. The UI reducer appends to
      // the matching tool-call part's subagentUpdates so ToolCallBlock
      // renders a live status list under the running task card.
      const v = (ev.value ?? {}) as {
        sessionId?: string
        childSessionId?: string
        parentCallId?: string
        kind?: string
        summary?: string
        detail?: string
      }
      if (!v.sessionId || !v.childSessionId || !v.kind || !v.summary) return []
      return [s({
        type: 'subagent-update',
        sessionId: v.sessionId,
        childSessionId: v.childSessionId,
        parentCallId: v.parentCallId,
        kind: v.kind as 'tool-start' | 'tool-done' | 'text' | 'completion' | 'error',
        summary: v.summary,
        detail: v.detail,
      }) as SE]
    }
    return []
  }
}
